import { toast } from "$global-providers/toast-provider";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  styled,
  TextField,
  Typography,
} from "@material-ui/core";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import { type FC, type FormEvent, useState } from "react";
import {
  EPOCUS_BORDER,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  rem,
} from "../../_lib/epocus-palette";
import {
  PROBE_ASSOCIATION_FAILURE_MESSAGES,
  PROBE_SERIAL_HINT,
  useManageProbeAssociations,
  useMyProbeAssociations,
} from "../../_lib/use-probe-associations";
import { SettingsCard, SettingsCardLabel } from "./settings-card";

const SerialRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  padding: "8px 0",
  borderBottom: `1px solid ${EPOCUS_BORDER}`,
});

const SerialValue = styled(Typography)({
  fontSize: rem(14),
  fontWeight: 600,
  color: EPOCUS_TEXT_PRIMARY,
  fontFamily: "monospace",
  wordBreak: "break-all",
});

const AddRow = styled(Box)({
  display: "flex",
  alignItems: "flex-start",
  gap: 8,
});

const HelpText = styled(Typography)({
  fontSize: rem(13),
  color: EPOCUS_TEXT_SECONDARY,
});

const SerialActionButton = styled(Button)({
  minHeight: 40,
  textTransform: "none",
  whiteSpace: "nowrap",
});

const EditRow: FC<{
  serial: string;
  saving: boolean;
  onSave: (serial: string) => void;
  onCancel: () => void;
}> = ({ serial, saving, onSave, onCancel }) => {
  const [value, setValue] = useState(serial);

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        onSave(value);
      }}
    >
      <AddRow>
        <TextField
          autoFocus
          fullWidth
          variant="outlined"
          margin="dense"
          label="Probe serial number"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          inputProps={{ "aria-label": `Edit probe serial ${serial}` }}
        />
        <SerialActionButton
          type="submit"
          variant="contained"
          color="primary"
          disableElevation
          disabled={saving || value.trim().length === 0}
          style={{ marginTop: 8 }}
        >
          {saving ? <CircularProgress size={16} /> : "Save"}
        </SerialActionButton>
        <SerialActionButton
          onClick={onCancel}
          disabled={saving}
          style={{ marginTop: 8 }}
        >
          Cancel
        </SerialActionButton>
      </AddRow>
    </form>
  );
};

export const ProbeAssociationsCard: FC = () => {
  const { associations, loading } = useMyProbeAssociations();
  const { add, update, remove, pendingSerial } = useManageProbeAssociations();
  const [serialInput, setSerialInput] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);

  const handleAdd = async (event: FormEvent) => {
    event.preventDefault();
    const failure = await add(serialInput);
    if (!failure) {
      setSerialInput("");
      toast.success("Probe added.");
      return;
    }
    toast.error(PROBE_ASSOCIATION_FAILURE_MESSAGES[failure]);
  };

  const handleUpdate = async (id: string, serial: string) => {
    const failure = await update(id, serial);
    if (!failure) {
      setEditingId(null);
      toast.success("Probe updated.");
      return;
    }
    toast.error(PROBE_ASSOCIATION_FAILURE_MESSAGES[failure]);
  };

  const handleRemove = async (id: string) => {
    const failure = await remove(id);
    if (!failure) {
      toast.success("Probe removed.");
      return;
    }
    toast.error("Couldn't remove that probe. Please try again.");
  };

  return (
    <SettingsCard>
      <SettingsCardLabel>Probes</SettingsCardLabel>
      <HelpText>
        Only scans from these probes appear in your To Document queue. Add every
        probe you scan with — you can share a probe with colleagues.
      </HelpText>

      {loading && associations.length === 0 ? (
        <Box display="flex" justifyContent="center" py={2}>
          <CircularProgress size={20} aria-label="Loading your probes" />
        </Box>
      ) : associations.length === 0 ? (
        <HelpText>No probes added yet.</HelpText>
      ) : (
        <Box>
          {associations.map((association) =>
            editingId === association.id ? (
              <EditRow
                key={association.id}
                serial={association.deviceSerialNumber}
                saving={pendingSerial !== null}
                onSave={(serial) => handleUpdate(association.id, serial)}
                onCancel={() => setEditingId(null)}
              />
            ) : (
              <SerialRow key={association.id}>
                <SerialValue>{association.deviceSerialNumber}</SerialValue>
                <Box display="flex" style={{ gap: 4 }}>
                  <IconButton
                    size="small"
                    onClick={() => setEditingId(association.id)}
                    aria-label={`Edit probe ${association.deviceSerialNumber}`}
                  >
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleRemove(association.id)}
                    aria-label={`Remove probe ${association.deviceSerialNumber}`}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              </SerialRow>
            ),
          )}
        </Box>
      )}

      <form onSubmit={handleAdd}>
        <AddRow>
          <TextField
            fullWidth
            variant="outlined"
            margin="dense"
            label="Probe serial number"
            value={serialInput}
            onChange={(event) => setSerialInput(event.target.value)}
            inputProps={{ "aria-label": "Probe serial number" }}
          />
          <SerialActionButton
            type="submit"
            variant="contained"
            color="primary"
            disableElevation
            disabled={pendingSerial !== null || serialInput.trim().length === 0}
            style={{ marginTop: 8 }}
          >
            {pendingSerial !== null ? (
              <CircularProgress size={16} />
            ) : (
              "Add probe"
            )}
          </SerialActionButton>
        </AddRow>
      </form>

      <HelpText>{PROBE_SERIAL_HINT}</HelpText>
    </SettingsCard>
  );
};
